import React, { useCallback, useEffect, useState } from 'react';
import { DatePicker, Select, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { TableWrapper } from 'container/styled';
import { CustomTableStyleWrapper } from 'pages/style';
import {
  CollectionChannelConfig,
  CollectionLogStatusConfig,
  CollectionActionTypeConfig,
} from 'constants/constants';
import {
  debtCollectionApi,
  DebtCollectionLogDto,
  CollectionChannel,
  CollectionActionType,
  CollectionLogStatus,
} from '../debtCollectionApi';

const { RangePicker } = DatePicker;

const LogsTab: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<DebtCollectionLogDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState({
    pageNumber: 1,
    pageSize: 10,
    channel: undefined as CollectionChannel | undefined,
    status: undefined as CollectionLogStatus | undefined,
    fromDate: undefined as string | undefined,
    toDate: undefined as string | undefined,
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await debtCollectionApi.getLogs(query);
      setLogs(data.items ?? []);
      setTotal(data.totalCount ?? 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns = [
    {
      title: t('debt-collection:logs.date', 'Date'),
      dataIndex: 'executedAt',
      key: 'executedAt',
      width: 170,
      render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
    },
    {
      title: t('debt-collection:logs.client', 'Client'),
      dataIndex: 'clientName',
      key: 'clientName',
      render: (val: string | null) => val || '-',
    },
    {
      title: t('debt-collection:logs.channel', 'Channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (val: CollectionChannel) => {
        const cfg = CollectionChannelConfig[val];
        return cfg ? <Tag color={cfg.color}>{cfg.title}</Tag> : '-';
      },
    },
    {
      title: t('debt-collection:logs.action', 'Action'),
      dataIndex: 'actionType',
      key: 'actionType',
      width: 130,
      render: (val: CollectionActionType) => {
        const cfg = CollectionActionTypeConfig[val];
        return cfg ? <Tag color={cfg.color}>{cfg.title}</Tag> : '-';
      },
    },
    {
      title: t('debt-collection:logs.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (val: CollectionLogStatus) => {
        const cfg = CollectionLogStatusConfig[val];
        return cfg ? <Tag color={cfg.color}>{cfg.title}</Tag> : '-';
      },
    },
    {
      title: t('debt-collection:logs.step', 'Step'),
      dataIndex: 'stepOrder',
      key: 'stepOrder',
      width: 70,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Select
          allowClear
          placeholder={t('debt-collection:logs.filter-channel', 'Filter by channel')}
          style={{ width: 180 }}
          value={query.channel}
          onChange={(val) => setQuery((prev) => ({ ...prev, channel: val, pageNumber: 1 }))}
        >
          {Object.entries(CollectionChannelConfig).map(([key, cfg]) => (
            <Select.Option key={key} value={Number(key)}>
              {cfg.title}
            </Select.Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder={t('debt-collection:logs.filter-status', 'Filter by status')}
          style={{ width: 160 }}
          value={query.status}
          onChange={(val) => setQuery((prev) => ({ ...prev, status: val, pageNumber: 1 }))}
        >
          {Object.entries(CollectionLogStatusConfig).map(([key, cfg]) => (
            <Select.Option key={key} value={Number(key)}>
              {cfg.title}
            </Select.Option>
          ))}
        </Select>

        <RangePicker
          onChange={(dates) => {
            setQuery((prev) => ({
              ...prev,
              fromDate: dates?.[0]?.toISOString(),
              toDate: dates?.[1]?.toISOString(),
              pageNumber: 1,
            }));
          }}
        />
      </div>

      <CustomTableStyleWrapper>
        <TableWrapper>
          <Table
            loading={loading}
            dataSource={logs}
            columns={columns}
            rowKey={(r) => String(r.id)}
            expandable={{
              expandedRowRender: (record: DebtCollectionLogDto) => (
                <div style={{ padding: '8px 0' }}>
                  {record.renderedMessage && (
                    <div style={{ marginBottom: 8 }}>
                      <strong>{t('debt-collection:logs.message', 'Message')}:</strong>
                      <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{record.renderedMessage}</div>
                    </div>
                  )}
                  {record.errorDetails && (
                    <div style={{ color: '#ff4d4f', marginBottom: 8 }}>
                      <strong>{t('debt-collection:logs.error', 'Error')}:</strong> {record.errorDetails}
                    </div>
                  )}
                  {record.externalMessageId && (
                    <div>
                      <strong>{t('debt-collection:logs.external-id', 'External ID')}:</strong>{' '}
                      {record.externalMessageId}
                    </div>
                  )}
                </div>
              ),
            }}
            pagination={{
              current: query.pageNumber,
              pageSize: query.pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: [10, 50, 100],
              showTotal: (totalCount, range) =>
                `${range[0]}-${range[1]} ${t('global:of', 'of')} ${totalCount} ${t('global:items', 'items')}`,
              onChange: (pageNumber, pageSize) =>
                setQuery((prev) => ({ ...prev, pageNumber, pageSize: pageSize || 10 })),
            }}
          />
        </TableWrapper>
      </CustomTableStyleWrapper>
    </div>
  );
};

export default LogsTab;
